// listPagination.js
//
// Single source of truth for the "paginated list + infinite scroll" wiring
// shared by the admin list screens. Built once per component (in its
// constructor) and delegated to, in the same instance-injection style as
// folderWiring.js:
//
//   this.list = makePaginatedList(this, {
//     endpoint, responseKey, listKey, pageKey, hasMoreKey, loadingKey,
//   })
//
// The returned object exposes:
//   - fetchPage(page, sortParamsOverride) -> fetch one page (replace on page 1,
//     dedupe-append after that), with a per-instance sequence guard
//   - handleScrollBottom()                -> load the next page, guarded
//   - reset()                             -> back to page 1
//   - attachMobileScroll() / detachMobileScroll()
//
// The server owns the sort, so it is sent with every page request and a sort
// change is a page-1 refetch — an in-memory sort would only reorder the pages
// already loaded.

import axios from '../../../axios'
import { isMobile } from '../../../utils/deviceInfo'

// Rows per request. One page has to fill the viewport, or the bottom-scroll
// listener never gets a chance to fire again.
export const DEFAULT_PAGE_LIMIT = 20
// Mobile window-scroll tuning (ported from the Library list page).
const SCROLL_THROTTLE_MS = 150
const SCROLL_THRESHOLD_PX = 250

export const makePaginatedList = (instance, config) => {
  const {
    endpoint,
    responseKey,
    listKey,
    pageKey,
    hasMoreKey,
    loadingKey,
    limit,
    extraParams,
  } = config

  const pageLimit = limit || DEFAULT_PAGE_LIMIT

  // Per-instance sequence guard: if a newer fetch (e.g. a page-1 reset after a
  // sort change, create or delete) starts while an older page-N append is still
  // in flight, the stale page-N response is dropped — otherwise it would append
  // rows past the reset and strand a window of rows permanently.
  let fetchSeq = 0
  let scrollThrottle = null

  const getList = (state) => {
    const list = state[listKey]
    if (!list) {
      return []
    }
    return list
  }

  // Fetch one page. Page 1 REPLACES the list (so every mutation/refresh caller
  // resets it); later pages append, deduped by _id so a cursor shift can never
  // surface the same row twice.
  const fetchPage = (page, sortParamsOverride, options) => {
    const opts = options || {}
    // refreshLoaded() re-fetches every already-loaded row as one page, so the
    // request limit and the page number we settle on afterwards can differ from
    // the defaults.
    const effectiveLimit = opts.limit || pageLimit
    const pageAfter = opts.pageAfter || page

    const seq = fetchSeq + 1
    fetchSeq = seq
    instance.setState({ [loadingKey]: true })

    // sortParamsOverride is passed on a sort change (props aren't updated yet
    // inside componentWillReceiveProps); otherwise use the current sort.
    const sortParams = sortParamsOverride || instance.props.sortParams || {}
    const params = { page: page, limit: effectiveLimit }
    if (sortParams.sortBy) {
      params.sortBy = sortParams.sortBy
      params.sortOrder = sortParams.sortOrder
    }
    if (typeof extraParams === 'function') {
      const extra = extraParams()
      if (extra) {
        Object.assign(params, extra)
      }
    }

    return axios
      .get(endpoint, { params })
      .then(({ data }) => {
        if (seq !== fetchSeq) {
          return
        }
        const payload = data && data.data ? data.data : {}
        let items = []
        if (Array.isArray(payload)) {
          items = payload
        } else if (payload[responseKey]) {
          items = payload[responseKey]
        }
        // hasMore comes from the response; fall back to a full page when the
        // key is absent (an older/unpaginated response shape).
        let hasMore
        if (!Array.isArray(payload) && payload.hasMore !== undefined) {
          hasMore = payload.hasMore === true
        } else {
          hasMore = items.length === effectiveLimit
        }

        instance.setState((prevState) => {
          let nextList
          if (page === 1) {
            nextList = items
          } else {
            const seen = new Set(
              getList(prevState).map((it) => String(it && it._id))
            )
            const fresh = items.filter(
              (it) => it && !seen.has(String(it._id))
            )
            nextList = [...getList(prevState), ...fresh]
          }
          return {
            [listKey]: nextList,
            [pageKey]: pageAfter,
            [hasMoreKey]: hasMore,
            [loadingKey]: false,
          }
        })
      })
      .catch((err) => {
        if (seq !== fetchSeq) {
          return
        }
        console.log('list page fetch failed', err)
        // Clear loading but deliberately leave hasMore alone. Setting it false
        // here would turn one transient failure (a timeout, a 500) into the rest
        // of the list being permanently unreachable for the session — scrolling
        // would stop for good. Leaving it lets the next bottom-scroll retry.
        instance.setState({ [loadingKey]: false })
      })
  }

  // Infinite-scroll handler for the base list (search keeps its own handler).
  const handleScrollBottom = () => {
    const state = instance.state
    if (state.isSearchActive || state[loadingKey] || !state[hasMoreKey]) {
      return
    }
    // Inside a folder the rows come from folderContents, not from this list, so
    // paging the base list here would fetch rows that cannot be displayed and
    // would keep inflating the page counter.
    if (state.activeFolder) {
      return
    }
    const currentPage = state[pageKey] || 1
    fetchPage(currentPage + 1)
  }

  // Back to page 1 — the list is replaced, not appended to.
  const reset = (sortParamsOverride) => fetchPage(1, sortParamsOverride)

  // Re-fetch exactly the rows already on screen, as a single page.
  //
  // Needed after any mutation that REMOVES a row from the server-side set —
  // delete, or moving a message into a folder (the list query is scoped to
  // folderId: null, so a foldered row leaves the set). Because paging is
  // skip-based, the server's offsets all shift by one and the next
  // bottom-scroll would step straight over exactly one row, which would then
  // never appear at all.
  //
  // Fetching page 1 alone would fix the offsets but throw away every page the
  // user had scrolled in, collapsing them back to the top. Requesting
  // loadedPages * pageLimit in one go keeps them where they were, and leaving
  // pageKey at the loaded count means the next scroll asks for the right
  // skip window.
  const refreshLoaded = (sortParamsOverride) => {
    const loadedPages = instance.state[pageKey] || 1
    if (loadedPages <= 1) {
      return fetchPage(1, sortParamsOverride)
    }
    return fetchPage(1, sortParamsOverride, {
      limit: loadedPages * pageLimit,
      pageAfter: loadedPages,
    })
  }

  // Mobile infinite-scroll: on mobile the list scrolls the PAGE (not an inner
  // container), so the bound BottomScrollListener never fires. Drive it off the
  // window scroll instead. Throttled; the handlers already guard on
  // loading/has-more so this can't over-fetch.
  const handleMobileWindowScroll = () => {
    if (scrollThrottle) {
      return
    }
    scrollThrottle = setTimeout(() => {
      scrollThrottle = null
      const node = document.scrollingElement || document.documentElement
      if (!node) {
        return
      }
      const distanceToBottom =
        node.scrollHeight - (node.scrollTop + window.innerHeight)
      if (distanceToBottom > SCROLL_THRESHOLD_PX) {
        return
      }
      if (
        instance.state.isSearchActive &&
        typeof instance.handleSearchScrollBottom === 'function'
      ) {
        instance.handleSearchScrollBottom()
      } else {
        handleScrollBottom()
      }
    }, SCROLL_THROTTLE_MS)
  }

  const attachMobileScroll = () => {
    if (!isMobile) {
      return
    }
    window.addEventListener('scroll', handleMobileWindowScroll, {
      passive: true,
    })
  }

  const detachMobileScroll = () => {
    window.removeEventListener('scroll', handleMobileWindowScroll)
    if (scrollThrottle) {
      clearTimeout(scrollThrottle)
      scrollThrottle = null
    }
  }

  return {
    fetchPage,
    handleScrollBottom,
    reset,
    refreshLoaded,
    attachMobileScroll,
    detachMobileScroll,
  }
}

export default makePaginatedList
